import { useCallback } from "react";
import { Country } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { TrackingEventCategory, useEmitUserEvent } from "@lookiero/sty-psp-tracking";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Order } from "../../../domain/entities/order/Order";
import { financial } from "../shared/util/financial/financial";
import { CheckoutSuccessTrackingEvent, Constants, PROJECT, TrackingEventName } from "./tracking";

interface TrackCheckoutSuccessFunctionArgs {
  readonly order: Order;
}

interface TrackCheckoutSuccessFunction {
  (args: TrackCheckoutSuccessFunctionArgs): void;
}

interface UseTrackCheckoutSuccessFunctionArgs {
  readonly country: Country;
  readonly segment: Segment;
  readonly userId: string;
  readonly tradename: Tradename;
}

interface UseTrackCheckoutSuccessFunction {
  (agrs: UseTrackCheckoutSuccessFunctionArgs): TrackCheckoutSuccessFunction;
}

const useTrackCheckoutSuccess: UseTrackCheckoutSuccessFunction = ({ country, segment, tradename, userId }) => {
  const emitUserEvent = useEmitUserEvent<CheckoutSuccessTrackingEvent>();

  const trackCheckoutSuccess: TrackCheckoutSuccessFunction = useCallback(
    ({ order }) => {
      emitUserEvent({
        event: TrackingEventName.CHECKOUT_SUCCESS,
        eventCategory: TrackingEventCategory.LOOK_IN,
        store: country,
        section: PROJECT,
        segment,
        tradename,
        userId,
        userUuid: userId,
        ecommerce: {
          currencyCode: order.currency,
          purchase: {
            actionField: {
              id: order.orderId,
              affiliation: "Lookiero Shop Complete Your Look",
              revenue: financial(order.totalPrice as number),
              tax: Constants.NOT_SET,
              shipping: 0,
              coupon: "",
            },
            products: order.items.map((item) => ({
              name: item.title,
              id: item.variantId,
              price: financial(item.price.amount),
              brand: item.brand,
              category: Constants.NOT_SET,
              variant: item.size,
              list: Constants.COMPLETE_YOUR_LOOK_LIST,
              quantity: Constants.QUANTITY,
            })),
          },
        },
      });
    },
    [country, emitUserEvent, segment, tradename, userId],
  );

  return trackCheckoutSuccess;
};

export { useTrackCheckoutSuccess };
